import {
    OTHER_CHARGE_DETALLE_MAX_LENGTH,
    StoredOtherCharge,
    generateOtherChargeId,
} from "@utils/DocumentOtherCharges";
import { ParsedEDocument, ParsedOtherCharge } from "./types";

/**
 * Convierte un cargo del XML a la forma que el sistema persiste.
 *
 * `StoredOtherCharge` no es lo mismo que `ParsedOtherCharge`: agrega `id` y `mode`, y renombra
 * casi todo (`tipoDocumento` -> `tipoDocumentoOC`, `porcentaje` -> `porcentajeOC`, y la
 * identificacion del tercero se anida). `normalizeStoredOtherCharge` (`@utils/DocumentOtherCharges`)
 * descarta en silencio toda entrada cuyo `tipoDocumentoOC` no sea del catalogo, asi que guardar
 * la forma del parser equivale a guardar un documento sin cargos: la fila "Otros Cargos" sale
 * en cero al imprimirlo.
 *
 * Los campos opcionales se omiten en vez de guardarse vacios, igual que hace
 * `toOtrosCargosElement`: un nodo vacio no pasa el XSD si el documento se reenviara.
 */
export const toStoredOtherCharge = (cargo: ParsedOtherCharge): StoredOtherCharge => {
    const { tipo, numero } = cargo.identificacionTercero;
    // El comprobante ya fue aceptado por Hacienda: `montoCargo` conserva siempre el monto
    // emitido, y `mode` solo describe como se calculo en su momento.
    const mode = cargo.porcentaje > 0 ? "percent" : "amount";
    return {
        id: generateOtherChargeId(),
        mode,
        tipoDocumentoOC: cargo.tipoDocumento,
        tipoDocumentoOTROS: cargo.tipoDocumentoOtros || undefined,
        identificacionTercero: tipo || numero ? { tipo, numero } : undefined,
        nombreTercero: cargo.nombreTercero || undefined,
        detalle: cargo.detalle.slice(0, OTHER_CHARGE_DETALLE_MAX_LENGTH),
        porcentajeOC: mode === "percent" ? cargo.porcentaje : undefined,
        montoCargo: cargo.montoCargo,
    };
};

export const toStoredOtherCharges = (parsed: ParsedEDocument): StoredOtherCharge[] =>
    parsed.otrosCargos.map(toStoredOtherCharge);

/** `otros_cargos` listo para el modelo, o `undefined` cuando el comprobante no trae ninguno. */
export const serializeParsedOtherCharges = (parsed: ParsedEDocument): string | undefined => {
    const charges = toStoredOtherCharges(parsed);
    return charges.length ? JSON.stringify(charges) : undefined;
};
